import { useMemo } from 'react';

import {
  useCurrentUser,
  useJobCountSince,
  useProfiles,
  useStripePlans,
  useSubscriptionStatus,
  useUserDocument,
  useUserLimits,
} from '@chartai/api-client';
import {
  type Profile,
  type ProfileStats,
  type TopProfile,
  type UserStats,
  type UserTemplateStats,
} from '@chartai/types';

import { type AccountUsage, StatsBanner } from '@/shared/components/StatsBanner';
import { UsageNudgeBanner } from '@/shared/components/UsageNudgeBanner';

/**
 * CAB-369 (frontend-only partial): trial users see a 250-minute quota.
 * Paid users see "Unlimited" — no number to compare against. The full
 * per-billing-period rollup + plan-driven quota lives in a follow-up
 * backend ticket; for this release we accept that:
 *   - `used` is all-time per-profile audio (not "this period"), and
 *   - a trial user can exceed 250 in the display.
 * Both are tracked with the user; product OK with this.
 */
const TRIAL_QUOTA_MIN = 250;
const BASIC_QUOTA_MIN = 1000;
/** CAB-368 locked decision: each chart represents ~5 saved minutes. */
const SAVED_MINUTES_PER_CHART = 5;

/**
 * Returns midnight Sunday of the current week in local time. Used as the
 * lower bound for "this week" job-count queries.
 */
function startOfWeek(now: Date): Date {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

function pickMostUsedTemplate(templates: Record<string, UserTemplateStats>): string | null {
  const entries = Object.entries(templates);
  if (entries.length === 0) {
    return null;
  }
  const sorted = [...entries].sort((a, b) => {
    if (b[1].count !== a[1].count) {
      return b[1].count - a[1].count;
    }
    return a[1].templateId.localeCompare(b[1].templateId);
  });
  return sorted[0]?.[1].displayName ?? null;
}

function sumTemplateCounts(templates: Record<string, UserTemplateStats>): number {
  return Object.values(templates).reduce((acc, t) => acc + (t.count || 0), 0);
}

function sumProfileAudioMinutes(profiles: Profile[]): number {
  // Sum seconds first, then convert. Per-profile rounding loses sub-minute
  // audio (e.g. three profiles with 6, 30, 175 seconds round to 0+1+3 = 4
  // when the real total is round(211/60) = 4 as well — but a profile with
  // 6 seconds alone would be lost as 0).
  const totalSeconds = profiles.reduce((acc, p) => {
    const stats: ProfileStats | null = p.stats ?? null;
    return acc + (stats?.audioSeconds ?? 0);
  }, 0);
  return Math.round(totalSeconds / 60);
}

/**
 * Light-blue header strip with the dashboard stat cards.
 *
 * Wires the shared StatsBanner to real CAB-365/366 data:
 *   - users/{uid}.stats.templates → Charts Completed + Most-Used Template
 *   - users/{uid}.topProfile + profile count → Top Performer (hidden for solo users)
 *   - users/{uid}/profiles/{*}.stats.audioSeconds → Account Usage
 *   - jobs created this week → Time Saved This Week
 *
 * Pages that use this should also negate the dashboard layout's outer
 * padding so the strip extends edge-to-edge:
 *   <div className="-m-4 lg:-m-6">
 *     <StatsHeader />
 *     <div className="p-4 lg:p-6">{page content}</div>
 *   </div>
 */
export function StatsHeader() {
  const { user } = useCurrentUser();
  const uid = user?.uid ?? null;

  const { data: userDoc } = useUserDocument(uid);
  const { data: profiles } = useProfiles(uid ?? '');
  // CAB-369 partial: drives the Account Usage card kind. Paid users
  // (Stripe `active` / `trialing` / `paid` / `ok`) get "Unlimited";
  // everyone else gets the trial quota display.
  const { isActive } = useSubscriptionStatus(uid ?? '');

  const sinceMonday = useMemo(() => startOfWeek(new Date()), []);
  const { data: jobsThisWeek } = useJobCountSince(uid ?? '', sinceMonday);

  const stats = (userDoc?.['stats'] as UserStats | null | undefined) ?? null;
  const topProfile = (userDoc?.['topProfile'] as TopProfile | null | undefined) ?? null;
  const templatesMap = stats?.templates ?? {};
  // useProfiles returns DocumentData[] (loose Firestore shape). Cast to
  // the typed Profile[] — runtime fields match because we only read
  // `stats` (added in CAB-365) which the cast tolerates.
  const profilesList = (profiles ?? []) as unknown as Profile[];

  const chartsCompleted = sumTemplateCounts(templatesMap);
  const mostUsedTemplate = pickMostUsedTemplate(templatesMap);
  const timeSavedHours = ((jobsThisWeek ?? 0) * SAVED_MINUTES_PER_CHART) / 60;

  const subObj =
    typeof userDoc?.['subscription'] === 'object' && userDoc['subscription'] !== null
      ? (userDoc['subscription'] as Record<string, unknown>)
      : null;
  const rawPriceId =
    subObj !== null && typeof subObj['priceId'] === 'string' ? subObj['priceId'] : '';

  // CAB-400: use per-period counter when available; fall back to all-time
  // per-profile sum for users not yet backfilled or on free plans.
  const periodSeconds =
    typeof stats?.audioSecondsThisPeriod === 'number' ? stats.audioSecondsThisPeriod : null;
  const accountUsageMinutes =
    periodSeconds !== null ? Math.round(periodSeconds / 60) : sumProfileAudioMinutes(profilesList);

  // CAB-400: read quota from Stripe price metadata; fall back to hardcoded default.
  const rawQuotaMinutes =
    subObj !== null && typeof subObj['quotaMinutes'] === 'number' ? subObj['quotaMinutes'] : null;

  // Basic-plan price IDs come from Firestore (useStripePlans) — the single
  // source of truth shared with the pricing/billing pages. Previously this read
  // the hardcoded getV2Plans(), whose price IDs were stale/USD.
  const { plans } = useStripePlans();
  const basicPriceIds = useMemo(() => {
    return new Set(
      plans
        .filter((p) => p.key === 'basic')
        .flatMap((p) => [p.monthlyPriceId, p.yearlyPriceId].filter(Boolean) as string[]),
    );
  }, [plans]);
  const isBasicPlan = rawPriceId ? basicPriceIds.has(rawPriceId) : false;

  const effectiveQuotaMin =
    rawQuotaMinutes !== null && rawQuotaMinutes > 0 ? rawQuotaMinutes : BASIC_QUOTA_MIN;

  // CAB-603: users/{uid}/limits/current is the authoritative counter — backend
  // written, not client-writable, and scoped to the current period rather than
  // all time. It is what CAB-605 will enforce against, so the display must come
  // from the same place.
  //
  // But only when the document actually exists. `synthesised` means there was
  // none, which is every user predating the backfill; trusting the synthesised
  // free/250 record would show a paying customer a 250-minute cap between this
  // deploy and the backfill. Until then, fall back to the pre-CAB-603 logic.
  const { limits, usedMinutes, isUnlimited } = useUserLimits(uid ?? null);
  const hasLimitsDoc = limits.synthesised !== true;

  const legacyAccountUsage: AccountUsage = !isActive
    ? { kind: 'quota', used: accountUsageMinutes, total: TRIAL_QUOTA_MIN }
    : isBasicPlan
      ? { kind: 'quota', used: accountUsageMinutes, total: effectiveQuotaMin }
      : { kind: 'unlimited', used: accountUsageMinutes };

  const accountUsage: AccountUsage = !hasLimitsDoc
    ? legacyAccountUsage
    : isUnlimited
      ? { kind: 'unlimited', used: usedMinutes }
      : { kind: 'quota', used: usedMinutes, total: limits.quotaMinutes ?? TRIAL_QUOTA_MIN };

  // CAB-368 locked decision: hide Top Performer for users with < 2
  // profiles OR when topProfile hasn't been computed yet. The local
  // `topProfile != null` check below also narrows the type for TS.
  const topPerformer =
    profilesList.length >= 2 && topProfile !== null
      ? {
          displayName: topProfile.displayName ?? '—',
          chartCount: topProfile.chartCount,
        }
      : null;

  return (
    <div className="bg-schedule-header shrink-0 p-4 lg:p-6">
      {/* CAB-606: renders nothing unless the user is between 75% and 100% of a
          real quota. Sits above the stats rather than inside a card, so the
          warning is not something you have to go looking for. */}
      <UsageNudgeBanner />
      <StatsBanner
        timeSavedHours={timeSavedHours}
        chartsCompleted={chartsCompleted}
        topPerformer={topPerformer}
        mostUsedTemplate={mostUsedTemplate}
        accountUsage={accountUsage}
      />
    </div>
  );
}
